import { todayPT } from "@homebound/activesupport";
import {
  ActionItemBuildAddressFragment,
  PersonalDashboard_ScheduleFlagFragment,
  PersonalDashboard_TaskFragment,
  PersonalDashboard_TradePartnerAvailabilityRequestFragment,
} from "src/fragments/circularFragments.generated";
// We have a ton of users with hundreds of stale tasks associated to abandoned/test projects.
// Adding a filter for 30 days ago as a simple fix to this issue.
import { NamedFragment } from "src/fragments/commonFragments.generated";
import { TradePartnerAvailabilityRequestStatus, TradePartnerTaskStatus } from "src/generated/graphqlTypes";
import { createDevelopmentScheduleUrl, createDraftScheduleUrl, createProjectScheduleUrl } from "src/RouteUrls";
import { assertNever, isDefined } from "src/utils";
import { nonClosedProjects } from "src/utils/projects";

export function thirtyDaysAgo() {
  return todayPT().subtract({ days: 30 });
}

// helper function to get the first name from InternalUser.
export function getFirstNameFromInternalUser(currentInternalUser: string) {
  const firstName = currentInternalUser?.split(" ")[0];
  return { firstName };
}

export function getTaskParentTitleAndUrl(taskParent: NamedFragment) {
  switch (taskParent.__typename) {
    case "Region":
    case "Market":
    case "Community":
    case "Subdivision":
    case "Lot":
    case "Structure":
      return { parentTitle: taskParent.name, url: createDraftScheduleUrl(taskParent.id) };
    case "StandaloneProject":
    case "Unit":
      return { parentTitle: taskParent.name, url: createProjectScheduleUrl(taskParent.id) };
    case "Development":
    case "ScheduleTemplate":
      return { parentTitle: taskParent.name, url: createDevelopmentScheduleUrl(taskParent.id) };
    case undefined:
      return { parentTitle: "", url: "" };
    default:
      assertNever(taskParent as never);
  }
}

// filter out projects from tasks on delay flags that are on hold or closed
export function filterScheduleFlagsByNonClosedStatus(scheduleFlags: PersonalDashboard_ScheduleFlagFragment[]) {
  return scheduleFlags?.filter(
    ({ task }) => isDefined(task?.project) && nonClosedProjects.includes(task.project.status.code),
  );
}

function getFullBuildAddress(buildAddress: ActionItemBuildAddressFragment) {
  const { city, postalCode, state, street1 } = buildAddress;
  return `${street1}, ${city} ${state} ${postalCode}`;
}

function getTradePartnerAvailabilityRequestEmailGreeting(
  scheduleTask: PersonalDashboard_TaskFragment,
  prevRequest: PersonalDashboard_TradePartnerAvailabilityRequestFragment | undefined,
) {
  const rescheduleDates = prevRequest?.rescheduleDates ?? [];

  if (
    scheduleTask.tradePartnerStatus.code === TradePartnerTaskStatus.Unavailable &&
    rescheduleDates.some((d) => d.equals(scheduleTask.interval.startDate))
  ) {
    return "We received your new availability for the task below and updated our schedule to match the date(s) that you’re available. We would greatly appreciate your availability for the new task date.";
  }

  if (scheduleTask.tradePartnerStatus.code === TradePartnerTaskStatus.NeedsConfirmation) {
    if (prevRequest?.status.code === TradePartnerAvailabilityRequestStatus.RescheduleNeeded) {
      return `We reviewed the new task date(s) that you provided availability on, but are unable to accommodate that date(s). Please review the new date(s) and let us know if you have availability for it.`;
    }
  }

  if (scheduleTask.tradePartnerStatus.code === TradePartnerTaskStatus.NeedsReconfirmation) {
    return `We changed the date of a task that you had already confirmed. We would greatly appreciate your availability for the new task date.`;
  }

  return "We would greatly appreciate your availability for the task below.";
}

function getTradePartnerAvailabilityRequestEmailFooter(
  scheduleTask: PersonalDashboard_TaskFragment,
  prevRequest: PersonalDashboard_TradePartnerAvailabilityRequestFragment | undefined,
) {
  const rescheduleDates = prevRequest?.rescheduleDates ?? [];

  if (
    scheduleTask.tradePartnerStatus.code === TradePartnerTaskStatus.Unavailable &&
    rescheduleDates.some((d) => d.equals(scheduleTask.interval.startDate))
  ) {
    return "Please review the new task dates and confirm that you are available. Thank you!";
  }

  return "Please confirm if you are available for the tasks in the link below. Thank you!";
}

export function getTradePartnerAvailabilityRequestEmailMessage(scheduleTask: PersonalDashboard_TaskFragment) {
  const { tradePartner, name, project, tradePartnerAvailabilityRequests } = scheduleTask;
  const prevRequest = tradePartnerAvailabilityRequests?.sortByKey("createdAt").last;
  const text = getTradePartnerAvailabilityRequestEmailGreeting(scheduleTask, prevRequest);
  const footer = getTradePartnerAvailabilityRequestEmailFooter(scheduleTask, prevRequest);

  return `<div>
  <!--block-->
  Hello ${tradePartner?.name ?? ""},<br><br>
  We are reaching out regarding an upcoming construction task. ${text}<br>
  <br><br>
  Project Details:&nbsp;
</div>
<ul>
  <li><!--block-->Project Name: ${project?.name}</li>
  <li><!--block-->Location: ${getFullBuildAddress(project?.buildAddress!)}</li>
  <li><!--block-->Construction Task: ${name}</li>
</ul>
<div>
  <!--block-->
  ${footer}
</div>`;
}

export function getTradePartnerAvailabilityRequestEmailSubject(scheduleTask: PersonalDashboard_TaskFragment) {
  const { tradePartnerAvailabilityRequests, name } = scheduleTask;
  return tradePartnerAvailabilityRequests?.nonEmpty
    ? `Important: Follow Up Request Availability for Upcoming Work (${name})`
    : `Request Availability for Upcoming Work (${name})`;
}
